import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Component, lazy, Suspense, useState, type ErrorInfo, type ReactNode } from 'react';
import { BrowserRouter, Navigate, Route, Routes } from 'react-router';
import { AuthProvider } from './auth/AuthProvider';
import { RequireAuth } from './components/Layout';
import { t, useI18n } from './i18n';
import { reportError } from './telemetry';
import { errorStatus } from './utils/errors';

// Route-level code splitting keeps the initial bundle to the shell + login.
const LoginPage = lazy(() => import('./pages/LoginPage'));
const TicketListPage = lazy(() => import('./pages/TicketListPage'));
const NewTicketPage = lazy(() => import('./pages/NewTicketPage'));
const TicketDetailPage = lazy(() => import('./pages/TicketDetailPage'));
const NotificationsPage = lazy(() => import('./pages/NotificationsPage'));
const DashboardPage = lazy(() => import('./pages/DashboardPage'));
const ReportsPage = lazy(() => import('./pages/ReportsPage'));
const SettingsPage = lazy(() => import('./pages/SettingsPage'));
const ForgotPasswordPage = lazy(() => import('./pages/PasswordPages').then((m) => ({ default: m.ForgotPasswordPage })));
const ResetPasswordPage = lazy(() => import('./pages/PasswordPages').then((m) => ({ default: m.ResetPasswordPage })));
// The whole admin area is one lazily loaded chunk: most users never download it.
const admin = () => import('./pages/AdminPages');
const AdminLayout = lazy(() => admin().then((m) => ({ default: m.AdminLayout })));
const StaffAdminOnly = lazy(() => admin().then((m) => ({ default: m.StaffAdminOnly })));
const UsersPage = lazy(() => admin().then((m) => ({ default: m.UsersPage })));
const OrganizationsPage = lazy(() => admin().then((m) => ({ default: m.OrganizationsPage })));
const SlaPolicyPage = lazy(() => admin().then((m) => ({ default: m.SlaPolicyPage })));
const AuditPage = lazy(() => admin().then((m) => ({ default: m.AuditPage })));
const BrandingPage = lazy(() => import('./pages/AdminCompliance').then((m) => ({ default: m.BrandingPage })));
const RolesPage = lazy(() => import('./pages/AdminCatalog').then((m) => ({ default: m.RolesPage })));
const CategoriesPage = lazy(() => import('./pages/AdminCatalog').then((m) => ({ default: m.CategoriesPage })));
const PrivacyRequestsPage = lazy(() => import('./pages/AdminCompliance').then((m) => ({ default: m.PrivacyRequestsPage })));

function createQueryClient(): QueryClient {
  const noRetryOnClientError = (count: number, err: unknown) => {
    const status = errorStatus(err);
    return (status === undefined || status >= 500) && count < 2;
  };
  return new QueryClient({
    defaultOptions: {
      queries: { staleTime: 30_000, gcTime: 5 * 60_000, retry: noRetryOnClientError, refetchOnWindowFocus: true },
      mutations: { retry: false },
    },
  });
}

class ErrorBoundary extends Component<{ children: ReactNode }, { failed: boolean }> {
  override state = { failed: false };
  static getDerivedStateFromError() {
    return { failed: true };
  }
  override componentDidCatch(error: Error, info: ErrorInfo) {
    reportError(Object.assign(error, { stack: `${error.stack ?? ''}\n${info.componentStack ?? ''}` }));
  }
  override render() {
    if (this.state.failed) {
      return (
        <main className="page">
          <h1>{t('Something went wrong')}</h1>
          <button type="button" className="btn btn-primary" onClick={() => window.location.reload()}>
            {t('Reload')}
          </button>
        </main>
      );
    }
    return this.props.children;
  }
}

export function AppRoutes() {
  useI18n();
  return (
    <Suspense fallback={<p className="page muted" role="status">{t('Loading…')}</p>}>
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />
        <Route element={<RequireAuth />}>
          <Route path="/tickets" element={<TicketListPage />} />
          <Route path="/tickets/new" element={<NewTicketPage />} />
          <Route path="/tickets/:id" element={<TicketDetailPage />} />
          <Route path="/dashboard" element={<DashboardPage />} />
          <Route path="/reports" element={<ReportsPage />} />
          <Route path="/notifications" element={<NotificationsPage />} />
          <Route path="/settings" element={<SettingsPage />} />
          <Route path="/admin" element={<AdminLayout />}>
            <Route index element={<Navigate to="users" replace />} />
            <Route path="users" element={<UsersPage />} />
            <Route path="organizations" element={<StaffAdminOnly><OrganizationsPage /></StaffAdminOnly>} />
            <Route path="sla" element={<StaffAdminOnly><SlaPolicyPage /></StaffAdminOnly>} />
            <Route path="audit" element={<StaffAdminOnly><AuditPage /></StaffAdminOnly>} />
            <Route path="roles" element={<RolesPage />} />
            <Route path="categories" element={<CategoriesPage />} />
            <Route path="branding" element={<BrandingPage />} />
            <Route path="privacy" element={<PrivacyRequestsPage />} />
          </Route>
        </Route>
        <Route path="*" element={<Navigate to="/dashboard" replace />} />
      </Routes>
    </Suspense>
  );
}

export function App() {
  const [queryClient] = useState(createQueryClient);
  return (
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <AuthProvider>
          <BrowserRouter>
            <AppRoutes />
          </BrowserRouter>
        </AuthProvider>
      </QueryClientProvider>
    </ErrorBoundary>
  );
}
